"use client"

import { useCallback } from "react"
import { useRouter } from "next/navigation"
import { motion } from "framer-motion"
import { X, ShoppingBag, Clock, Flame } from "lucide-react"
import type { Dish } from "@/lib/api"
import { useApp } from "@/lib/context"
import { useModalA11y } from "@/lib/use-modal-a11y"
import AddToDraftButton from "./add-to-draft-button"
import PriceDisplay from "./price-display"

interface DishDetailModalProps {
  dish: Dish
  onClose: () => void
  /** Controlado externamente (ex.: templates); o modal também funciona sem ele. */
  isOpen?: boolean
  /** Classe de z-index — usar acima do overlay que abre o modal (default "z-50"). */
  zClassName?: string
}

export default function DishDetailModal({ dish, onClose, zClassName = "z-50" }: DishDetailModalProps) {
  const router = useRouter()
  const { t, addToDraft, setShowDraft, draftCount } = useApp()

  const stableClose = useCallback(() => onClose(), [onClose])
  const dialogRef = useModalA11y<HTMLDivElement>(true, stableClose)

  // Tentar analisar os ingredientes
  let ingredientsList: string[] = []
  try {
    if (dish.ingredientes && dish.ingredientes.length > 0) {
      const firstIngredient = dish.ingredientes[0]
      if (firstIngredient.startsWith("[")) {
        ingredientsList = JSON.parse(firstIngredient)
      } else {
        ingredientsList = dish.ingredientes
      }
    }
  } catch (e) {
    console.error("Erro ao analisar ingredientes:", e)
    ingredientsList = dish.ingredientes || []
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={`fixed inset-0 ${zClassName} flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto`}
      onClick={onClose}
    >
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={dish.nome}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-card text-card-foreground w-full min-h-screen md:min-h-0 md:max-h-[90vh] md:max-w-2xl md:my-8 md:rounded-2xl overflow-hidden shadow-2xl border border-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 w-full">
          <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden bg-secondary">
            <motion.img
              src={dish.urls[0] || "/placeholder.svg"}
              alt={dish.nome}
              className="w-full h-full object-cover"
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6 }}
              onError={(e) => {
                e.currentTarget.src = "/placeholder.svg"
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-black/30" />

            {dish.destacado && (
              <div className="absolute top-4 left-4 rounded-full bg-primary/95 px-3 py-1 text-primary-foreground flex items-center gap-1 backdrop-blur">
                <Flame className="w-3.5 h-3.5" />
                <span className="text-xs uppercase tracking-wider">{t("featured")}</span>
              </div>
            )}

            <div className="absolute top-3 right-3 flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  // O pedido vive numa página completa
                  setShowDraft(false)
                  router.push("/pedido")
                }}
                className="relative rounded-full bg-white/20 p-2 text-white backdrop-blur-md transition-colors hover:bg-white/40"
                aria-label={t("viewDraft")}
              >
                <ShoppingBag className="w-5 h-5" />
                {draftCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {draftCount}
                  </span>
                )}
              </button>
              <button
                onClick={onClose}
                className="rounded-full bg-white/20 p-2 text-white backdrop-blur-md transition-colors hover:bg-white/40"
                aria-label={t("close")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <motion.div
              className="absolute bottom-0 left-0 right-0 p-4 sm:p-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <h2 className="text-xl font-serif font-light text-white drop-shadow-[0_1px_10px_rgba(0,0,0,0.5)] sm:text-3xl">
                {dish.nome}
              </h2>
              {dish.tempo_preparo > 0 && (
                <div className="mt-1 flex items-center gap-1.5 text-sm text-white/80">
                  <Clock className="w-3.5 h-3.5" />
                  <span>
                    {t("preparationTime")}: {dish.tempo_preparo} {t("minutes")}
                  </span>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        <div className="space-y-5 p-4 pb-24 sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <PriceDisplay
              dish={dish}
              className="text-xl font-bold text-primary sm:text-2xl"
            />
            <AddToDraftButton dish={dish} onClick={() => addToDraft(dish)} size="lg" />
          </div>

          {dish.descricao && (
            <div>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("description")}
              </h3>
              <p className="text-sm leading-relaxed text-foreground/80">{dish.descricao}</p>
            </div>
          )}

          {ingredientsList.length > 0 && (
            <div>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("ingredients")}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {ingredientsList.map((ingredient, index) => (
                  <span
                    key={index}
                    className="rounded-full border border-border bg-secondary px-2.5 py-1 text-xs text-foreground/75"
                  >
                    {ingredient}
                  </span>
                ))}
              </div>
            </div>
          )}

          {dish.urls.length > 1 && (
            <div>
              <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                {t("moreImages")}
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {dish.urls.slice(1).map((url, index) => (
                  <div key={index} className="aspect-square overflow-hidden rounded-lg border border-border">
                    <img
                      src={url || "/placeholder.svg"}
                      alt={`${dish.nome} ${index + 2}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder.svg"
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}
